<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import _ from 'lodash';
import { OPENJAW } from '@/constants';
import { getTextVariant } from '@/utils/stringUtils';
import { useClientCopyComposable } from './useClientCopyComposable';
import { useModalComposable } from '@/views/composable';
import { useI18nLocalize } from '@/i18n/i18nLocalize';
import { useMyQuoteStore } from '@/store/MyQuoteStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import AgencyInformation from '@/components/modal/clientCopy/AgencyInformation.vue';
import ClientCopyFlightDetail from '@/components/modal/clientCopy/ClientCopyFlightDetail.vue';
import ClientCopyHeader from '@/components/modal/clientCopy/ClientCopyHeader.vue';
import HTMLEditor from '@/components/common/HTMLEditor.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import PricingAir from '@/components/modal/clientCopy/PricingAir.vue';

interface Props {
  show: boolean;
  quote: object;
}
const props = defineProps<Props>();

const data = computed(() => props.quote);
const { i18nLocale } = useI18nLocalize();
const { t, $t } = i18nLocale(data.value.lang);
const airClone = ref([]);
const logo = ref();
const isEditPricing = ref([]);
const isEdit = ref(false);
const isLoading = ref(false);
const isPriceValid = ref(true);
const toast = useToast();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const viewQuoteStore = useViewQuoteStore();
const myQuoteStore = useMyQuoteStore();
const { flightImage } = useClientCopyComposable(data.value?.lang, data.value?.currency);
const { handleOpenErrorMessage } = useModalComposable();

const handleSubmitClientCopy = async () => {
  if (isEdit.value) {
    toast.add({ severity: 'info', summary: '', detail: t('client_copy.save_price_before_submit'), life: 3000 });
    return;
  }
  if (!isPriceValid.value) {
    handleOpenErrorMessage(t('client_copy.new_price_bigger'));
    return;
  }
  isLoading.value = true;
  const quoteCopy = _.cloneDeep(props.quote);
  quoteCopy.urlLogo = logo.value || quoteCopy.urlLogo;
  quoteCopy.airs = quoteCopy?.airs?.map((_air, idxAir) => {
    return airClone.value[idxAir];
  });
  await viewQuoteStore.handleSubmitClientCopy(quoteCopy);
  isLoading.value = false;
};

const sumEconomy = (airsData) => {
  return airsData.reduce((total, flight) => {
    return total + (flight.adtNo ?? 0) + (flight.chdNo ?? 0);
  }, 0);
};

onBeforeMount(() => {
  props.quote?.airs?.forEach((air, i: number) => {
    airClone.value[i] = { ...air };
    isEditPricing.value[i] = [];
    air.price?.forEach((_p, indexA) => {
      isEditPricing.value[i][indexA] = false;
    });
  });
});
</script>
<template>
  <Transition name="modal">
    <div v-if="show" class="client-copy">
      <div class="modal-mask">
        <LoadingSpinner v-if="isLoading" />
        <div class="relative modal-container client-copy-air" @click.stop>
          <div class="sticky top-0">
            <div class="absolute right-0 flex align-items-center justify-content-center w-3rem h-3rem border-round">
              <Button class="text-900" icon="pi pi-times" severity="success" text rounded @click="$emit('close')" />
            </div>
          </div>
          <div class="wrapper py-4 px-5">
            <div class="wrapper">
              <table class="w-full">
                <thead>
                  <tr>
                    <th class="hidden" v-for="time in 3" :key="time"></th>
                  </tr>
                </thead>
                <tbody>
                  <ClientCopyHeader v-model:logo="logo" :url="`${myQuoteStore?.getUrlUploadImg}?clientCopy=true`" :data="data" />
                  <AgencyInformation :data="data" :totalRoom="0" :sumEconomy="sumEconomy" />
                </tbody>
              </table>
              <table>
                <thead>
                  <tr>
                    <th class="hidden"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(air, index) in data.airs" :key="index">
                    <td>
                      <table class="w-full">
                        <thead>
                          <tr>
                            <th class="hidden" v-for="time in 3" :key="time"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td colspan="3">
                              <div class="pt-3 flex">
                                <img alt="img" :src="flightImage" class="height-25px" />
                                <div class="text-4xl font-bold">
                                  {{ $t('common.option') }} <span>{{ index + 1 }}</span
                                  >:
                                  {{ `${$t('client_copy.adults_count', air.adtNo + air.businessAdtNo)}, ${$t('client_copy.children_count', air.chdNo + air.businessChdNo)}` }}
                                </div>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td class="text-2xl pt-1">
                              <span class="font-bold">{{ $t(`advance_quote.form.${air.itinerary.toLowerCase()}`) }}: </span>
                              <span>{{ getTextVariant(props.quote.lang, air.gate, air.gateFr) }}</span> {{ $t('common.to_normal') }}
                              <span>{{ getTextVariant(props.quote.lang, air.dest, air.destFr) }}</span>
                              <span v-if="air.itinerary === OPENJAW">
                                <span> / {{ air.returnFrom }}</span> {{ $t('common.to_normal') }}
                                <span>{{ air.returnTo }}</span>
                              </span>
                            </td>
                          </tr>
                          <tr class="break-before">
                            <td colspan="3" class="pt-3 text-xl">
                              {{ $t('common.flight_details') }} <span v-if="air.pnr">({{ $t('common.reference') }}: {{ air.pnr }})</span>
                            </td>
                          </tr>
                          <tr>
                            <td class="text-base">{{ `${$t('common.labels.departure_date')}: ${air.flights[0].depDate}` }} | {{ `${$t('client_copy.return_date')}: ${air.flights[air.flights.length - 1].depDate}` }}</td>
                          </tr>
                          <tr>
                            <td colspan="3" class="pt-2">
                              <ClientCopyFlightDetail :flights="air.flights" :lang="data?.lang" :isAir="true" />
                            </td>
                          </tr>
                          <tr>
                            <td colspan="3" class="pt-3 text-xl">
                              <span class="flex justify-content-between align-items-center">
                                <div>{{ $t('common.pricing') }} *</div>
                                <div class="text-xs">*{{ $t('client_copy.pricing_is_per_person') }}</div>
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td colspan="3">
                              <PricingAir
                                :airClone="airClone"
                                :index="index"
                                :lang="data?.lang"
                                :currency="data?.currency"
                                :priceField="'price'"
                                v-model:isEdit="isEdit"
                                v-model:isPriceValid="isPriceValid"
                                v-model:isEditPricing="isEditPricing"
                              ></PricingAir>
                            </td>
                          </tr>
                          <tr>
                            <td colspan="3">
                              <HTMLEditor class="mb-4 w-full mt-1" v-model.trim="airClone[index].comment" />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="action-submit text-left">
              <Button v-if="authorizations?.my_quote?.client_copy_edit" :label="$t('common.buttons.submit')" class="mt-5" @click="handleSubmitClientCopy()" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
